// app/components/Contact.tsx
"use client";

import { useEffect, useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useRouter } from "next/navigation";

const GREEN = "rgb(34 150 83)";

const CLINICS = [{ id: 2, name: "Pinak Dental Clinic - Dinnur, RT Nagar", address: "Dinnur Main Road, R.T. Nagar", city: "Bengaluru, Karnataka - 560032", phone: "+918404007002", email: "pinakdentalclinicblr@gmail.com", mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d31259.464142049153!2d77.56154000759123!3d13.02374470447953!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae17f70d058539%3A0xcc2306059bf59a20!2sPINAK%20DENTAL%20CLINIC%20ADVANCE%20DENTAL%20CARE%2C%20ROOT%20CANAL%2C%20IMPLANTS%2C%20INVISALIGN%2C%20BRACES%20-%20DINNUR%20ROAD%20BRANCH!5e1!3m2!1sen!2sin!4v1769753007051!5m2!1sen!2sin", mapsLink: "https://www.google.com/maps/place/PINAK+DENTAL+CLINIC+ADVANCE+DENTAL+CARE,+ROOT+CANAL,+IMPLANTS,+INVISALIGN,+BRACES+-+DINNUR+ROAD+BRANCH/@13.0237447,77.56154,7178m/data=!3m1!1e3!4m10!1m2!2m1!1spinak+dental+clinic+bengaluru!3m6!1s0x3bae17f70d058539:0xcc2306059bf59a20!8m2!3d13.0237282!4d77.5996434!15sCh1waW5hayBkZW50YWwgY2xpbmljIGJlbmdhbHVydZIBDWRlbnRhbF9jbGluaWPgAQA!16s%2Fg%2F11xlqfvmrv?entry=ttu&g_ep=EgoyMDI2MDEyNy4wIKXMDSoKLDEwMDc5MjA2OUgBUAM%3D", },];

const SERVICES = [
  "Dental Implants",
  "Full Mouth Rehabilitation (FMR)",
  "Smile Designing",
  "Porcelain Veneers",
  "Braces, Aligners & Invisalign",
  "Root Canal Treatment (RCT)",
  "Tooth Extraction",
  "Teeth Whitening",
  "Crowns & Bridges",
  "Pediatric Dentistry",
];

export default function Contact() {
  const router = useRouter();
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { once: true, margin: "-10%" });

  const [loading, setLoading] = useState(false);
  const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setServiceDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.service) {
      alert("Please select a treatment before submitting.");
      return;
    }
    setLoading(true);

    const res = await fetch("/api/send-mail", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...formData,
        clinic: CLINICS[0].name,
      }),
    });

    setLoading(false);

    if (res.ok) router.push("/thank-you");
    else alert("Something went wrong. Please try again.");
  };

  return (
    <section
      ref={rootRef}
      id="contact"
      className="relative overflow-hidden bg-gradient-to-b from-gray-900 via-emerald-950 to-gray-900 pt-12 pb-24"
    >
      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-12">
        {/* ================= HEADER (UNCHANGED) ================= */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <span className="inline-block rounded-full border-2 border-emerald-400/50 bg-emerald-500/20 px-6 py-2 text-sm font-bold text-emerald-300 backdrop-blur">
            📞 Get In Touch
          </span>

          <h2 className="mt-6 text-5xl font-black text-white lg:text-6xl">
            Book Your{" "}
            <span className="bg-gradient-to-r from-emerald-400 to-cyan-300 bg-clip-text text-transparent">
              Appointment
            </span>
          </h2>

          <p className="mx-auto mt-4 max-w-2xl text-xl text-gray-400">
            Visit our clinic or send us a message — we&apos;re here to help with all your dental care needs.
          </p>
        </motion.div>

        {/* ================= GRID ================= */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* ================= MAPS (LEFT ON DESKTOP, BELOW ON MOBILE) ================= */}
          <div className="order-2 lg:order-1">
            {CLINICS.map((clinic) => (
              <motion.div
                key={clinic.id}
                onClick={() => window.open(clinic.mapsLink, "_blank")}
                className="group cursor-pointer"
              >
                <motion.div
                  className="relative h-[300px] lg:h-[450px] overflow-hidden rounded-3xl border-2 border-emerald-500/30"
                  whileHover={{ scale: 1.02, y: -5 }}
                >
                  {/* Click overlay */}
                  <a
                    href={clinic.mapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute inset-0 z-30"
                  />

                  {/* Map */}
                  <iframe
                    src={clinic.mapUrl}
                    className="absolute inset-0 size-full pointer-events-none"
                    loading="lazy"
                    style={{
                      border: 0,
                      filter:
                        "grayscale(20%) brightness(0.4) contrast(1.2)",
                    }}
                  />

                  {/* Overlay */}
                  <div className="absolute inset-0 z-10 bg-gradient-to-t from-gray-900/90 via-gray-900/60 to-transparent" />

                  {/* Content */}
                  <div className="relative z-40 flex h-full flex-col justify-end p-6">
                    <h3 className="text-2xl font-bold text-white">
                      {clinic.name}
                    </h3>

                    <p className="mt-2 text-sm text-gray-300">
                      {clinic.address}
                      <br />
                      {clinic.city}
                    </p>

                    <div className="mt-3 space-y-1 text-sm">
                      <a
                        href={`tel:${clinic.phone}`}
                        className="block font-semibold text-emerald-400 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        📞 {clinic.phone}
                      </a>

                      <a
                        href={`mailto:${clinic.email}`}
                        className="block font-semibold text-emerald-400 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        ✉️ {clinic.email}
                      </a>

                      <p className="mt-2 text-gray-300">
                        🕒 Mon–Sat: 10 AM – 10 PM | Sun: 11 AM – 1 PM
                      </p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>

          {/* ================= FORM (RIGHT ON DESKTOP, FIRST ON MOBILE) ================= */}
          <motion.div className="order-1 lg:order-2">
            <form
              onSubmit={handleSubmit}
              className="rounded-3xl border-2 border-emerald-500/30 bg-gradient-to-br from-gray-800/80 to-gray-900/80 p-8 backdrop-blur"
            >
              <h3 className="mb-6 text-2xl font-bold text-white">
                Send Us a Message
              </h3>

              <div className="space-y-5">
                <input
                  type="text"
                  placeholder="Full Name"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full rounded-xl bg-gray-900 px-4 py-3 text-white"
                />

                <input
                  type="email"
                  placeholder="Email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full rounded-xl bg-gray-900 px-4 py-3 text-white"
                />

                <input
                  type="tel"
                  placeholder="Phone Number"
                  required
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="w-full rounded-xl bg-gray-900 px-4 py-3 text-white"
                />

                <div ref={dropdownRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setServiceDropdownOpen((open) => !open)}
                    className="w-full rounded-xl bg-gray-900 px-4 py-3 text-left text-white"
                  >
                    {formData.service || "Select Treatment"}
                  </button>

                  {serviceDropdownOpen && (
                    <motion.ul
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      className="absolute left-0 right-0 z-20 mt-2 max-h-60 overflow-y-auto rounded-2xl border border-gray-700 bg-gray-900 shadow-2xl"
                    >
                      {SERVICES.map((service) => (
                        <li key={service}>
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, service });
                              setServiceDropdownOpen(false);
                            }}
                            className="w-full px-4 py-3 text-left text-white hover:bg-gray-800"
                          >
                            {service}
                          </button>
                        </li>
                      ))}
                    </motion.ul>
                  )}
                </div>

                <textarea
                  rows={4}
                  placeholder="Tell us about your dental needs..."
                  required
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      message: e.target.value,
                    })
                  }
                  className="w-full rounded-xl bg-gray-900 px-4 py-3 text-white"
                />

                <motion.button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full py-4 font-bold text-white"
                  style={{ backgroundColor: GREEN }}
                  whileHover={{ scale: 1.03 }}
                >
                  {loading ? "Sending..." : "Book Appointment →"}
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
